import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import API_BASE_URL from "../config";
import { ClipLoader } from "react-spinners";
import {
  FiSearch, FiBookOpen, FiCalendar, FiCreditCard, FiShield, FiFileText,
  FiHome, FiClipboard, FiDollarSign,
} from "react-icons/fi";

// GuestLinkup theme: navy (--primary: 55,55,88) and green (--secondary: 28,173,112)
const NAVY = "#373758";
const GREEN = "#1cad70";

// Each real guide gets an icon and tint that reflect what it's actually about,
// not just a repeated category placeholder. Keyed by slug since that's stable
// and specific; category is only a fallback for guides not curated here yet.
const ARTICLE_VISUALS = {
  "listing-your-homestay-on-guestlinkup": { icon: FiHome, bg: "#eaf6f0", fg: GREEN },
  "inventory-management": { icon: FiClipboard, bg: "#eef0f7", fg: NAVY },
};

const CATEGORY_VISUALS = {
  "Getting Started": { icon: FiBookOpen, bg: "#eaf6f0", fg: GREEN },
  "Bookings": { icon: FiCalendar, bg: "#eef0f7", fg: NAVY },
  "Payments": { icon: FiDollarSign, bg: "#fdf2e9", fg: "#c98a2e" },
  "Policies": { icon: FiShield, bg: "#e9f4f7", fg: "#2f7d94" },
};

const DEFAULT_VISUAL = { icon: FiFileText, bg: "#f4f6f5", fg: NAVY };

const getArticleVisual = (article) =>
  ARTICLE_VISUALS[article.slug] || CATEGORY_VISUALS[article.category] || DEFAULT_VISUAL;

const HelpCenter = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState("All");

  useEffect(() => {
    const fetchArticles = async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}help-center/articles`);
        setArticles(res.data.data || []);
      } catch (err) {
        console.error("Failed to load help center guides", err);
      } finally {
        setLoading(false);
      }
    };
    fetchArticles();
  }, []);

  const categories = useMemo(
    () => Array.from(new Set(articles.map((a) => a.category).filter(Boolean))),
    [articles]
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return articles.filter((a) => {
      const matchesCategory = activeCategory === "All" || a.category === activeCategory;
      const matchesQuery =
        !q || a.title.toLowerCase().includes(q) || (a.summary || "").toLowerCase().includes(q);
      return matchesCategory && matchesQuery;
    });
  }, [articles, activeCategory, query]);

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "50vh", width: "100%" }}>
        <ClipLoader size={45} />
      </div>
    );
  }

  return (
    <>
      <div className="innerbanner"></div>

      {/* HERO */}
      <section className="py-5" style={{ background: "linear-gradient(180deg, #eaf6f0 0%, #f6fbfb 100%)" }}>
        <div className="container text-center">
          <div className="text-uppercase fw-bold small mb-2" style={{ color: GREEN, letterSpacing: "0.14em" }}>
            Help Center
          </div>
          <h1 className="fw-bold mb-4" style={{ color: NAVY, fontSize: "2.4rem" }}>
            Hi there, how can we <span style={{ color: GREEN }}>help?</span>
          </h1>

          <div className="mx-auto mb-4" style={{ maxWidth: 620 }}>
            <div className="d-flex align-items-center bg-white rounded-pill shadow-sm px-4 py-2">
              <FiSearch style={{ color: "#9c9c9c", marginRight: 10, flexShrink: 0 }} size={18} />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search guides..."
                className="form-control border-0 shadow-none p-0"
                style={{ background: "transparent" }}
              />
            </div>
          </div>

          {categories.length > 0 && (
            <div className="d-flex flex-wrap justify-content-center gap-2">
              <button
                onClick={() => setActiveCategory("All")}
                className="btn rounded-pill px-3 py-2 small fw-semibold border-0"
                style={{
                  background: activeCategory === "All" ? NAVY : "#fff",
                  color: activeCategory === "All" ? "#fff" : NAVY,
                }}
              >
                All Guides
              </button>
              {categories.map((c) => (
                <button
                  key={c}
                  onClick={() => setActiveCategory(c)}
                  className="btn rounded-pill px-3 py-2 small fw-semibold border-0"
                  style={{
                    background: activeCategory === c ? NAVY : "#fff",
                    color: activeCategory === c ? "#fff" : NAVY,
                  }}
                >
                  {c}
                </button>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* TABS + GRID */}
      <section className="py-5" style={{ background: "#fff" }}>
        <div className="container">
          {categories.length > 0 && (
            <div className="d-flex flex-wrap gap-4 mb-4" style={{ borderBottom: "1px solid #e5eceb" }}>
              {["All", ...categories].map((c) => (
                <button
                  key={c}
                  onClick={() => setActiveCategory(c)}
                  className="btn btn-link text-decoration-none px-0 pb-2"
                  style={{
                    color: activeCategory === c ? NAVY : "#9c9c9c",
                    fontWeight: activeCategory === c ? 700 : 500,
                    borderBottom: activeCategory === c ? `2px solid ${GREEN}` : "2px solid transparent",
                    borderRadius: 0,
                  }}
                >
                  {c === "All" ? "All Help" : c}
                </button>
              ))}
            </div>
          )}

          <div className="row g-4">
            {filtered.length === 0 && (
              <div className="col-12 text-center text-muted py-5">
                {articles.length === 0 ? "No guides published yet — check back soon." : "No guides match your search."}
              </div>
            )}
            {filtered.map((article) => {
              const { icon: Icon, bg, fg } = getArticleVisual(article);
              return (
                <div className="col-lg-3 col-md-6" key={article.id}>
                  <Link
                    to={`/help-center/${article.slug}`}
                    className="d-flex flex-column h-100 text-decoration-none shadow-sm"
                    style={{ background: "#fff", border: "1px solid #eef0ee", borderRadius: 16, overflow: "hidden" }}
                  >
                    {article.cover_image ? (
                      <div style={{ height: 130, overflow: "hidden" }}>
                        <img
                          src={article.cover_image}
                          alt={article.title}
                          style={{ width: "100%", height: "100%", objectFit: "cover" }}
                        />
                      </div>
                    ) : (
                      <div className="d-flex align-items-center justify-content-center" style={{ height: 130, background: bg }}>
                        <Icon size={34} style={{ color: fg }} />
                      </div>
                    )}
                    <div className="p-4 d-flex flex-column flex-grow-1">
                      {article.category && (
                        <span
                          className="small fw-bold text-uppercase mb-2"
                          style={{ color: fg, letterSpacing: "0.05em" }}
                        >
                          {article.category}
                        </span>
                      )}
                      <h6 className="fw-bold mb-2" style={{ color: NAVY }}>{article.title}</h6>
                      {article.summary && (
                        <p className="text-muted small mb-3" style={{ flexGrow: 1 }}>{article.summary}</p>
                      )}
                      <div className="fw-bold small mt-auto" style={{ color: GREEN }}>Read guide &rarr;</div>
                    </div>
                  </Link>
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </>
  );
};

export default HelpCenter;
